import { useCurrentProject } from "@/features/projects/current-project";
import { useProjects } from "@/features/projects/queries";
import * as Popover from "@radix-ui/react-popover";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Settings } from "lucide-react";

export function ProjectSwitcher() {
  const { projectId, projectName, setCurrent } = useCurrentProject();
  const { data } = useProjects();
  const qc = useQueryClient();

  const projects = data?.items ?? [];

  const pick = (id: string, name: string) => {
    setCurrent(id, name);
    // Invalidate per-project caches so new project's data loads fresh.
    qc.invalidateQueries();
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-left text-[13px] hover:bg-surface-2"
        >
          <div className="grid size-[22px] shrink-0 place-items-center rounded bg-accent/20 font-mono text-[10px] font-semibold text-accent">
            {(projectName ?? "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 truncate">
            <div className="text-[10px] uppercase tracking-wider text-fg-dim">Project</div>
            <div className="truncate text-[13px] font-medium">
              {projectName ?? <span className="italic text-fg-dim">None</span>}
            </div>
          </div>
          <ChevronsUpDown size={12} className="shrink-0 text-fg-dim" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="start"
          className="z-50 w-[220px] rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="max-h-[320px] overflow-y-auto p-1.5">
            {projects.length === 0 && (
              <div className="px-3 py-2 text-xs text-fg-muted">No projects</div>
            )}
            {projects.map((p) => (
              <Popover.Close key={p.id} asChild>
                <button
                  type="button"
                  onClick={() => pick(p.id, p.name)}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs hover:bg-surface-2"
                >
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.id === projectId && <Check size={12} className="text-accent" />}
                </button>
              </Popover.Close>
            ))}
          </div>
          <div className="border-t border-border p-1.5">
            <Popover.Close asChild>
              <Link
                to="/projects"
                className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                <Settings size={12} />
                Manage projects
              </Link>
            </Popover.Close>
          </div>
          <Popover.Arrow className="fill-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
